import React, { useEffect, useState } from "react";
import { joinClasses } from "ds-core/utils";
import { Carousel, getCurrentViewport } from "fr-ito-web-react/react/esm";
import { ILinkProps } from "fr-ito-web-react/components";
import { ECImageProps } from "@common/NewImage";
import { ECCarousel } from "@common/index";
import { useViewportWidth } from "./helpers";
import style from "./styles.module.css";

export enum ImagePlusTextCollectionVariant {
  verticalExtraSmall = "verticalExtraSmall"
}

export enum ImagePlusTextVariant {
  verticalExtraSmall = "verticalExtraSmall"
}

export type ImagePlusTextProps = {
  variant: ImagePlusTextVariant;
  className?: string;
  key?: string;
  media: React.ReactElement<ECImageProps>;
  imagePosition?: "left" | "right";
  title?: string;
  linkText?: string;
  description?: string;
  caption?: string; // implement later, support line break; Works only for horizontalLarge and small variant;
  flag?: string; // for the “New” flag. Works only for horizontalLarge and small variant
  selected?: boolean; // Works only for lineup and verticalExtraSmall variants
  children?: React.ReactNode;
  onClick?: () => void;
  buttonLabel?: string;
};

type Props = React.HTMLAttributes<HTMLElement> & {
  variant: ImagePlusTextCollectionVariant;
  children?:
    | React.ReactElement<ILinkProps>[]
    | React.ReactElement<ImagePlusTextProps>[];
};

type ImagePlusTextCollectionSlidesPerView =
  ImagePlusTextCollectionVariant.verticalExtraSmall;

const SLIDES_PER_VIEW: Record<
  ImagePlusTextCollectionSlidesPerView,
  { LARGE: number; MEDIUM: number; SMALL: number }
> = {
  [ImagePlusTextCollectionVariant.verticalExtraSmall]: {
    LARGE: 7.5,
    MEDIUM: 6.5,
    SMALL: 3.2
  }
};

const getSlidesPerView = (
  variant: ImagePlusTextCollectionSlidesPerView,
  width: number
) => {
  const slidesConfig = SLIDES_PER_VIEW[variant];
  if (width >= 960) return slidesConfig.LARGE;
  if (width >= 600) return slidesConfig.MEDIUM;
  return slidesConfig.SMALL;
};

export const ECImagePlusTextCollection: React.FC<Props> = (props: Props) => {
  const {
    children,
    variant = ImagePlusTextCollectionVariant.verticalExtraSmall,
    className
  } = props;
  const viewportSize = getCurrentViewport();
  const isSpOrTablet = viewportSize === "sm" || viewportSize === "md";
  const viewportWidth = useViewportWidth(); // Call the hook here
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [slidesPerView, setSlidesPerView] = useState(
    getSlidesPerView(variant, viewportWidth)
  );
  const totalChildren = React.Children.count(children);
  const slideCount =
    totalChildren <= slidesPerView ? 0 : totalChildren - slidesPerView + 1;

  useEffect(() => {
    const handleResize = () => {
      setSlidesPerView(getSlidesPerView(variant, viewportWidth));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [variant, viewportWidth]);

  return (
    <ECCarousel
      className={joinClasses(
        style.ecImagePlusTextCollectionCarousel,
        className
      )}
      index={activeIndex}
      hasArrows={{ sm: false, md: false, lg: true }}
      onIndexChange={(index: number) => {
        setActiveIndex(index);
      }}
      indicator={
        <Carousel.Indicator
          activeIndex={activeIndex}
          variant="dots"
          slideCount={slideCount}
        />
      }
      displayCount={{
        sm: SLIDES_PER_VIEW.verticalExtraSmall.SMALL,
        md: SLIDES_PER_VIEW.verticalExtraSmall.MEDIUM,
        lg: SLIDES_PER_VIEW.verticalExtraSmall.LARGE
      }}
      swiperOptions={{
        freeMode: isSpOrTablet,
        spaceBetween: 8
      }}
    >
      {children}
    </ECCarousel>
  );
};
